import { useMemo, useState } from "react";
import { Trash2, TriangleAlert } from "lucide-react";
import { Button } from "@shared/ui/Button";
import type { MentionCandidate } from "@shared/ui/MentionCandidate";
import { MentionInput } from "@shared/ui/MentionInput";
import { Section } from "@shared/ui/Section";
import { TextInput } from "@shared/ui/TextInput";
import { confirm } from "@shared/ui/confirm/confirmStore";
import { PaneHeader } from "@features/pane-manager/PaneHeader";
import { PaneFooter } from "@features/pane-manager/PaneFooter";
import { usePaneManager } from "@features/pane-manager/store";
import { useScrollMemory } from "@features/pane-manager/useScrollMemory";
import { useCurrentSelection, useDeck } from "@features/office";
import {
    generationSettingStore,
    assignmentInertReason,
    makeElementRef,
    makeSlideRef,
    parseMentions,
    writeAssignmentInstruction,
    refKindLabel,
    createTargetNameResolver,
    useElements,
    useAssignment,
    useSlides,
    type Assignment,
    type TargetRef,
} from "@entities/generation-setting";

/**
 * 画面C。1 つの生成 AI への指示を書く。
 *
 * 対象は本文のメンションが決めるので、対象を選ぶ入力欄は置かない。実行ごとの旗
 * （対象ごとの書き込み可否・担当のスキップ）もここが持つ。フォーマットの恒久設定は画面B。
 * 保存は即時で、完了ボタンは置かない（離脱はヘッダーの戻る）。
 *
 * 設計: [[2026-08-26 設計ノート アドイン 生成設定UIの再構成]]。
 */
export function AssignmentResourcePan({ id }: { id: string }) {
    const assignment = useAssignment(id);
    const slides = useSlides();
    const closePane = usePaneManager((s) => s.closePane);
    const activePaneUid = usePaneManager((s) => s.activePaneUid);
    // 戻ってきたら離れたときの位置へ帰す（[[2026-09-04 設計ノート アドイン 戻ったときの表示位置]]）。
    const scroll = useScrollMemory();
    /** 対象も候補も枚をまたぐので、全枚を見る。走査は deckStore が 1 箇所で行う。 */
    const deck = useDeck();
    const selection = useCurrentSelection();
    const elements = useElements();
    /** 下書き（ストアに実体が無い状態）のラベル。1 文字入った時点で実体を作る。 */
    const [draftLabel, setDraftLabel] = useState("");
    const [draftInstruction, setDraftInstruction] = useState("");

    /**
     * 対象の表示名。名前の正本は settings で、無い枠だけ図形名へ落ちる。
     *
     * **枚も引ける表を使う。** 以前は図形だけの対応表を組んでいたため、名前を付けていない
     * 枚を名指すと対象一覧に識別子がそのまま出ていた（記録 69）。生成結果ペインと同じ
     * 関数へ寄せてある。
     */
    const nameOf = useMemo(
        () => createTargetNameResolver({ deck: deck.slides, slides, elements }),
        [deck, slides, elements]
    );

    /**
     * メンションの候補。全スライドと、その上の全図形から作る。
     * 編集中の枚を先頭へ寄せるのは、いま見ている紙面から書き始めることが多いため。
     */
    const candidates = useMemo<MentionCandidate[]>(() => {
        const out: MentionCandidate[] = [];
        const current = selection.slideId;
        const ordered = [...deck.slides].sort((a, b) => {
            if (current === null) return a.index - b.index;
            const ax = a.officeSlideId === current ? 0 : 1;
            const bx = b.officeSlideId === current ? 0 : 1;
            return ax - bx || a.index - b.index;
        });
        for (const s of ordered) {
            const slideRef = makeSlideRef(s.uid);
            out.push({
                ref: slideRef,
                name: nameOf(slideRef),
                badge: refKindLabel(slideRef),
                note: String(s.index),
            });
            for (const shape of s.shapes) {
                const elementRef = makeElementRef(shape.uid);
                out.push({
                    ref: elementRef,
                    name: nameOf(elementRef),
                    badge: refKindLabel(elementRef),
                    note: String(s.index),
                });
            }
        }
        return out;
    }, [deck, selection, nameOf]);

    const label = assignment?.label ?? draftLabel;
    function write(next: Partial<Assignment>): void {
        const base: Assignment = assignment ?? draft;
        const merged: Assignment = {
            ...base,
            ...next,
            updatedAt: new Date().toISOString(),
            updatedBy: "",
        };
        generationSettingStore.writeAssignment(id, merged);
    }

    function handleLabel(value: string): void {
        setDraftLabel(value);
        write({ label: value });
    }

    function handleInstruction(value: string): void {
        setDraftInstruction(value);
        // 作法は `writeAssignmentInstruction` が持つ（本文から対象を作り直し、書き込まない旗を
        // 同じ瞬間に絞る）。**生成結果の行からも同じ本文を直せる**ので、組み立てを 2 か所へ
        // 書くと片方だけ作り直しが抜ける。
        writeAssignmentInstruction(id, value, assignment ?? draft);
    }

    function toggleTarget(ref: TargetRef): void {
        const skipped = new Set(current.skipTargets ?? []);
        if (skipped.has(ref)) skipped.delete(ref);
        else skipped.add(ref);
        write({ skipTargets: skipped.size > 0 ? Array.from(skipped) : undefined });
    }

    async function handleDelete(): Promise<void> {
        const ok = await confirm({
            title: `「${label || "無題"}」を削除しますか？`,
            tone: "danger",
        });
        if (!ok) return;
        generationSettingStore.writeAssignment(id, null);
        if (activePaneUid) closePane(activePaneUid);
    }

    // 下書き（ストアに実体が無い状態）でも全項目を出す。ラベルを入れるまで本文すら
    // 書けない作りは、押した直後に何もできない画面を生む。実体を作らないのは
    // 保存の話であって、編集させない理由にはならない。
    const draft: Assignment = {
        label: draftLabel,
        instruction: draftInstruction,
        targets: parseMentions(draftInstruction),
        updatedAt: "",
        updatedBy: "",
    };
    const current = assignment ?? draft;
    const skipped = new Set(current.skipTargets ?? []);
    const inertReason = assignment ? assignmentInertReason(assignment) : null;
    /** ストアに実体がまだ無い状態。何か書けば保存される。 */
    const unsaved = assignment === undefined;

    return (
        <>
            <PaneHeader title={label.trim().length > 0 ? label : "新規"} />
            <div ref={scroll.ref} className="flex-1 overflow-y-auto p-3">
                <div className="pb-3">
                    <Section title="ラベル">
                        <TextInput
                            value={label}
                            onChange={handleLabel}
                            fullWidth
                            placeholder="担当の名前を入力…"
                        />
                        {unsaved && (
                            <p className="mt-1 text-[11px] text-text-muted">
                                何か入力すると保存されます
                            </p>
                        )}
                    </Section>
                </div>

                <>
                    {/* 本文を対象より先に置く。**本文が対象を決める**ので、原因を先に、
                        結果を後に並べる。逆に置くと、まだ何も無い対象欄を最初に見せて
                        「ここで対象を選ぶのだろう」と読ませてしまう。 */}
                    <div className="pb-3">
                        <Section title="本文">
                            <MentionInput
                                value={current.instruction}
                                onChange={handleInstruction}
                                candidates={candidates}
                                placeholder="どこへ何を書くかを、@ で対象を挙げながら書く"
                            />
                        </Section>
                    </div>

                    <div className="pb-3">
                        <Section
                            title="対象"
                            action={<span className="text-[11px] text-text-muted">書き込む</span>}
                        >
                            {current.targets.length === 0 ? (
                                <p className="text-xs text-text-muted">
                                    本文で @ を書くと、ここに対象が並びます
                                </p>
                            ) : (
                                <div className="overflow-hidden rounded-md border border-border-subtle bg-surface">
                                    {current.targets.map((ref) => {
                                        const off = skipped.has(ref);
                                        return (
                                            <label
                                                key={ref}
                                                className={`flex cursor-pointer items-center gap-2 border-t border-text-primary/8 px-3 py-1.5 first:border-t-0 ${off ? "opacity-50" : ""}`}
                                            >
                                                <span className="shrink-0 rounded bg-brand-light px-1.5 text-[11px] font-semibold text-brand-dark">
                                                    {refKindLabel(ref)}
                                                </span>
                                                <span className="min-w-0 flex-1 truncate text-[13px]">
                                                    {/* 引き当ての順（設定 → 走査結果 → 識別子）は
                                                        `createTargetNameResolver` が持つ。設定を
                                                        先に見るのは、走査に失敗しても名前が読める
                                                        ようにするためである（記録 54）。 */}
                                                    {nameOf(ref)}
                                                </span>
                                                <input
                                                    type="checkbox"
                                                    className="size-3.5 shrink-0 cursor-pointer accent-brand"
                                                    checked={!off}
                                                    onChange={() => toggleTarget(ref)}
                                                    aria-label="書き込む"
                                                />
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </Section>
                    </div>

                    <div className="pb-3">
                        <Section title="今回の実行">
                            <div className="overflow-hidden rounded-md border border-border-subtle bg-surface">
                                <label className="flex cursor-pointer items-center gap-2 px-3 py-2">
                                    <span className="min-w-0 flex-1 text-[13px]">
                                        この担当をスキップ
                                    </span>
                                    <input
                                        type="checkbox"
                                        className="size-3.5 shrink-0 cursor-pointer accent-brand"
                                        checked={current.skip === true}
                                        onChange={() =>
                                            write({
                                                skip: current.skip === true ? undefined : true,
                                            })
                                        }
                                        aria-label="この担当をスキップ"
                                    />
                                </label>
                                {inertReason !== null && inertReason !== "skip" && (
                                    <div className="flex items-start gap-2 border-t border-text-primary/8 bg-warning/10 px-3 py-2 text-xs text-warning">
                                        <TriangleAlert size={12} className="mt-0.5 shrink-0" />
                                        <span>
                                            {inertReason === "allTargetsSkipped"
                                                ? "全対象が書き込み対象外のため実行されません"
                                                : "対象がないため実行されません"}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </Section>
                    </div>
                </>
            </div>
            <PaneFooter>
                <span className="flex-1" />
                {assignment && (
                    <Button
                        variant="danger"
                        size="sm"
                        leftIcon={<Trash2 size={14} />}
                        onClick={() => void handleDelete()}
                    >
                        削除
                    </Button>
                )}
            </PaneFooter>
        </>
    );
}
